import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentArrowUpIcon, CheckCircleIcon, XCircleIcon, ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import { BlobReader, ZipReader, TextWriter } from "@zip.js/zip.js";
import { getCompanyProjects } from '../utils/supabaseClient';
import { useUpload } from '../context/UploadContext';

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024;
const MAX_FILE_SIZE_LABEL = '2 GB';
const ZIP_MAGIC_BYTES = [0x50, 0x4B, 0x03, 0x04];

const validateFileType = async (file) => {
  const buffer = await file.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(buffer);
  return ZIP_MAGIC_BYTES.every((b, i) => bytes[i] === b);
};

const parseProjectNameFromFile = async (file) => {
  try {
    const zipReader = new ZipReader(new BlobReader(file));
    const entries = await zipReader.getEntries();
    const headerEntry = entries.find(e => e.filename.endsWith('Header.csv'));
    if (!headerEntry) { await zipReader.close(); return file.name.replace(/\.(zip|acf)$/i, ''); }
    const headerContent = await headerEntry.getData(new TextWriter());
    await zipReader.close();
    const jobLine = headerContent.split('\n').find(line => line.trim().startsWith('Job ='));
    if (jobLine?.split('=')[1]?.trim()) return jobLine.split('=')[1].trim();
    const projectLine = headerContent.split('\n').find(line => line.trim().startsWith('Project ='));
    if (projectLine?.split('=')[1]?.trim()) return projectLine.split('=')[1].trim();
    return file.name.replace(/\.(zip|acf)$/i, '');
  } catch {
    return file.name.replace(/\.(zip|acf)$/i, '');
  }
};

const STATUS_CONFIG = {
  starting: { label: 'Starting...', color: 'text-gray-500', icon: ArrowPathIcon, spin: true },
  uploading: { label: 'Uploading', color: 'text-blue-600', icon: ArrowPathIcon, spin: true },
  pending: { label: 'Queued', color: 'text-yellow-600', icon: ClockIcon, spin: false },
  processing: { label: 'Processing', color: 'text-purple-600', icon: ArrowPathIcon, spin: true },
  complete: { label: 'Complete', color: 'text-green-600', icon: CheckCircleIcon, spin: false },
  failed: { label: 'Failed', color: 'text-red-600', icon: XCircleIcon, spin: false },
  error: { label: 'Error', color: 'text-red-600', icon: XCircleIcon, spin: false },
};

export default function UploadPage() {
  const navigate = useNavigate();
  const { startUpload, uploads } = useUpload();

  const [file, setFile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [uploadMode, setUploadMode] = useState('create');
  const [newProjectName, setNewProjectName] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isParsing, setIsParsing] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const userProjects = await getCompanyProjects();
        setProjects(userProjects);
        if (userProjects?.length > 0) {
          setSelectedProjectId(userProjects[0].id);
        } else {
          setUploadMode('create');
        }
      } catch {
        setError('Could not load your projects.');
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  const handleFileChange = useCallback(async (selectedFile) => {
    if (!selectedFile) return;
    setError('');

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setError(`File is too large. Maximum size is ${MAX_FILE_SIZE_LABEL}.`);
      return;
    }

    const isValid = await validateFileType(selectedFile);
    if (!isValid) {
      setError('Invalid file format. Please upload a valid .ACF or .ZIP file.');
      return;
    }

    setFile(selectedFile);
    setIsParsing(true);
    const suggestedName = await parseProjectNameFromFile(selectedFile);
    setNewProjectName(suggestedName);
    setIsParsing(false);
  }, []);

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (e.dataTransfer.files?.[0]) handleFileChange(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file) { setError('Please select a file.'); return; }
    if (uploadMode === 'create' && !newProjectName.trim()) { setError('Please enter a project name.'); return; }
    if (uploadMode === 'existing' && !selectedProjectId) { setError('Please select a project.'); return; }

    setIsSubmitting(true);
    startUpload({ file, uploadMode, newProjectName, selectedProjectId });

    // Reset form for next upload
    setTimeout(() => {
      setFile(null);
      setIsSubmitting(false);
      setNewProjectName('');
      setError('');
    }, 500);
  };

  const recentUploads = Object.values(uploads).sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));

  return (
    <div className="bg-white min-h-screen flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 h-14 bg-white border-b border-gray-200 px-6 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">Upload</h1>
        <p className="text-xs text-gray-400">Import .ACF files to create or update projects</p>
      </div>

      <div className="max-w-3xl mx-auto w-full py-6 px-6 flex-1">

        {/* Drop zone — always visible */}
        <label
          htmlFor="file-upload"
          className={`block rounded-lg border-2 border-dashed transition-all duration-200 cursor-pointer ${
            isDragging
              ? 'border-red-400 bg-red-50'
              : file
                ? 'border-green-300 bg-green-50'
                : 'border-gray-300 hover:border-gray-400 bg-white'
          } ${file ? 'p-4' : 'p-10'}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            id="file-upload"
            type="file"
            className="sr-only"
            accept=".zip,.acf,application/zip,application/x-zip-compressed"
            onChange={(e) => handleFileChange(e.target.files[0])}
          />

          {file ? (
            // File selected — compact view
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(1)} MB {isParsing && '— Reading metadata...'}</p>
                </div>
              </div>
              <button
                onClick={(e) => { e.preventDefault(); setFile(null); setError(''); setNewProjectName(''); }}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                Change
              </button>
            </div>
          ) : (
            // Empty — large drop zone
            <div className="text-center">
              <DocumentArrowUpIcon className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-3 text-sm font-medium text-gray-600">
                Drop .ACF file here or <span className="text-red-600">browse</span>
              </p>
              <p className="mt-1 text-xs text-gray-400">ZIP or ACF format, up to {MAX_FILE_SIZE_LABEL}</p>
            </div>
          )}
        </label>

        {/* Error */}
        {error && (
          <div className="mt-3 text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        {/* Configuration — appears after file selected */}
        {file && !isParsing && (
          <div className="mt-4 bg-white rounded-lg border border-gray-200 p-5">
            {/* Mode toggle */}
            <div className="flex items-center gap-1 p-0.5 bg-gray-100 rounded-md mb-4 w-fit">
              <button
                onClick={() => setUploadMode('create')}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  uploadMode === 'create' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                New Project
              </button>
              {projects.length > 0 && (
                <button
                  onClick={() => setUploadMode('existing')}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                    uploadMode === 'existing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Existing Project
                </button>
              )}
            </div>

            {/* Form */}
            {uploadMode === 'create' ? (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Project Name</label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-1 focus:ring-red-500 focus:border-red-500 bg-white"
                  placeholder="e.g., Highway 58 Resurfacing"
                />
                {newProjectName && (
                  <p className="mt-1.5 text-[11px] text-gray-400">Auto-detected from file metadata</p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Project</label>
                {loadingProjects ? (
                  <div className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 animate-pulse" />
                ) : (
                  <select
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-1 focus:ring-red-500 focus:border-red-500 bg-white"
                  >
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleUpload}
              disabled={isSubmitting || (uploadMode === 'existing' && loadingProjects)}
              className="mt-4 w-full px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Starting upload...' : uploadMode === 'create' ? 'Create Project & Upload' : 'Add to Project'}
            </button>
          </div>
        )}

        {/* Recent uploads */}
        {recentUploads.length > 0 && (
          <div className="mt-8">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Recent Uploads</h3>
            <div className="space-y-2">
              {recentUploads.map(upload => {
                const config = STATUS_CONFIG[upload.status] || STATUS_CONFIG.error;
                const Icon = config.icon;
                return (
                  <div key={upload.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className={`h-4 w-4 shrink-0 ${config.color} ${config.spin ? 'animate-spin' : ''}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{upload.file?.name || 'Upload'}</p>
                        {upload.error && <p className="text-xs text-red-500 truncate">{upload.error}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {upload.status === 'uploading' && upload.progress != null && (
                        <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${upload.progress}%` }} />
                        </div>
                      )}
                      <span className={`text-xs font-medium ${config.color}`}>
                        {upload.status === 'uploading' ? `${upload.progress || 0}%` : config.label}
                      </span>
                      {upload.status === 'complete' && upload.projectId && (
                        <button
                          onClick={() => navigate(`/projects/${upload.projectId}`)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          View
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
